'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LayoutDashboard, Send, Users, BarChart3, Settings, ChevronLeft, ChevronRight, LogOut, UserMinus, ShieldCheck, Radio, LayoutTemplate, Mail, Filter, GitBranch, FlaskConical } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';


const navGroups = [
  {
    label: 'Main',
    items: [
      { label: 'Dashboard', href: '/', icon: LayoutDashboard },
      { label: 'Campaigns', href: '/campaign-management', icon: Send, badge: 3 },
      { label: 'Subscribers', href: '/subscriber-management', icon: Users },
      { label: 'Segments', href: '/segments', icon: Filter, adminOnly: true },
      { label: 'Automation', href: '/automation', icon: GitBranch, adminOnly: true },
      { label: 'A/B Testing', href: '/ab-testing', icon: FlaskConical, adminOnly: true },
      { label: 'Delivery Monitor', href: '/delivery-monitor', icon: Radio },
      { label: 'Email Templates', href: '/email-templates', icon: LayoutTemplate },
      { label: 'Brevo', href: '/brevo', icon: Mail },
    ],
  },
  {
    label: 'Insights',
    items: [
      { label: 'Analytics', href: '/analytics', icon: BarChart3 },
      { label: 'Unsubscribes', href: '/unsubscribe-manager', icon: UserMinus },
    ],
  },
  {
    label: 'Account',
    items: [
      { label: 'Admin Panel', href: '/admin-panel', icon: ShieldCheck },
      { label: 'Settings', href: '/settings', icon: Settings },
    ],
  },
];

export default function Sidebar() {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);
  const { isAdmin } = useAuth();

  return (
    <aside
      className={`sidebar-transition flex-shrink-0 h-screen border-r border-border flex flex-col relative ${
        collapsed ? 'w-16' : 'w-60'
      }`}
      style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f9f7ff 100%)' }}
    >
      {/* Logo */}
      <div
        className={`flex items-center h-16 border-b border-border px-3 ${collapsed ? 'justify-center' : 'gap-2.5'}`}
        style={{ background: 'linear-gradient(135deg, rgba(91,53,232,0.06) 0%, rgba(255,92,43,0.04) 100%)' }}
      >
        <div className="flex-shrink-0 w-8 h-8 rounded-xl overflow-hidden flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #5b35e8 0%, #8b5cf6 60%, #ff5c2b 100%)', boxShadow: '0 2px 10px rgba(91,53,232,0.35)' }}
        >
          <AppImage
            src="/assets/appnest-flow-logo.svg"
            alt="AppNest Flow logo"
            width={22}
            height={22}
            className="flex-shrink-0"
            priority
            unoptimized
          />
        </div>
        {!collapsed && (
          <div className="flex flex-col leading-none">
            <span className="font-extrabold text-sm tracking-tight" style={{ color: '#5b35e8' }}>
              AppNest
            </span>
            <span className="font-bold text-xs tracking-widest uppercase" style={{ color: '#ff5c2b', letterSpacing: '0.12em' }}>
              Flow
            </span>
          </div>
        )}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed((p) => !p)}
        className="absolute -right-3 top-20 z-10 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center shadow-sm hover:bg-secondary transition-all duration-150"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight size={12} className="text-muted-foreground" />
        ) : (
          <ChevronLeft size={12} className="text-muted-foreground" />
        )}
      </button>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2 space-y-4">
        {navGroups?.map((group) => (
          <div key={`group-${group?.label}`}>
            {!collapsed && (
              <p className="metric-label px-3 mb-1">{group?.label}</p>
            )}
            <ul className="space-y-0.5">
              {group?.items?.map((item) => {
                // Hide admin-only items from non-admin users
                if ((item as any).adminOnly && !isAdmin) return null;

                const Icon = item?.icon;
                const isActive =
                  item?.href === '/'
                    ? pathname === '/'
                    : pathname?.startsWith(item?.href);
                return (
                  <li key={`nav-${item?.href}`}>
                    <Link
                      href={item?.href}
                      className={isActive ? 'nav-item-active' : 'nav-item'}
                      title={collapsed ? item?.label : undefined}
                    >
                      <Icon size={18} className="flex-shrink-0" />
                      {!collapsed && (
                        <>
                          <span className="flex-1">{item?.label}</span>
                          {'badge' in item && item?.badge ? (
                            <span className="badge text-xs px-1.5 py-0.5 rounded-full font-bold text-white"
                              style={{ background: 'linear-gradient(135deg, #5b35e8, #ff5c2b)' }}>
                              {item?.badge}
                            </span>
                          ) : null}
                        </>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Upgrade nudge */}
      {!collapsed && (
        <div className="mx-2 mb-3 rounded-xl p-3 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #5b35e8 0%, #8b5cf6 60%, #ff5c2b 100%)' }}>
          <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #ffffff, transparent)' }} />
          <p className="text-xs font-bold text-white mb-0.5">AppNest Flow Pro</p>
          <p className="text-xs text-white/70 mb-2">Unlock 200K sends/day</p>
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/20 text-white text-xs font-semibold hover:bg-white/30 transition-all cursor-pointer">
            ✦ Upgrade
          </div>
        </div>
      )}

      {/* Bottom */}
      <div className="border-t border-border p-2 space-y-1">
        {!collapsed && (
          <div className="px-3 py-2 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold"
              style={{ background: 'linear-gradient(135deg, #5b35e8, #ff5c2b)' }}>
              MA
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground truncate">Marcus Allen</p>
              <p className="text-xs text-muted-foreground truncate">Admin</p>
            </div>
          </div>
        )}
        <Link
          href="/login-screen"
          className="nav-item w-full"
          title={collapsed ? 'Logout' : undefined}
        >
          <LogOut size={18} className="flex-shrink-0 text-danger" />
          {!collapsed && <span className="text-danger">Logout</span>}
        </Link>
      </div>
    </aside>
  );
}